/**
 * PasskeyIndstillinger.jsx
 *
 * Panel til at registrere og slette passkeys for den indloggede bruger.
 * Integreres i profil/indstillinger-siden.
 *
 * Understøtter to flows:
 *   1. Native Android (Capacitor APK): @capgo/capacitor-native-biometric
 *      — viser "Slet fingeraftryk-login" hvis credentials er gemt
 *   2. Web-browser: WebAuthn passkey via @simplewebauthn/browser
 *
 * Kræver:
 *   - Gyldig session (sv_token cookie)
 *   - Native: Capacitor platform + biometri konfigureret på enheden
 *   - Web: Browser med window.PublicKeyCredential (WebAuthn)
 *
 * Props: (ingen — bruger credentials: 'include' cookie automatisk)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  erNativeBiometriTilgaengelig,
  tjekBiometrisk,
  sletGemteCredentials,
  harGemteCredentials,
  gemCredentialsMedBiometri,
} from '../biometri-native';

async function hent_webauthn_browser() {
  const mod = await import('@simplewebauthn/browser');
  return mod;
}

// Web-fallback: WebAuthn kun i ikke-native kontekst
const er_native_android = erNativeBiometriTilgaengelig();
const PASSKEY_UNDERSTOETTES = er_native_android
  ? false  // native flow overtager — WebAuthn-sektionen skjules
  : typeof window !== 'undefined' && !!window.PublicKeyCredential;

export default function PasskeyIndstillinger() {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fejl, setFejl] = useState(null);
  const [registrerer, setRegistrerer] = useState(false);
  const [sletter_id, setSletterID] = useState(null);
  const [besked, setBesked] = useState(null);
  const [nyt_navn, setNytNavn] = useState('');
  // SIKKERHEDSFIX 2026-10-09: backend kræver nu frisk adgangskode-bekræftelse
  // før en ny passkey kan registreres (se routes/passkey.js, registrer-start) —
  // lukker et hul hvor en stjålet session kunne plante en permanent
  // passkey-bagdør uden at kende adgangskoden.
  const [registrer_pw, setRegistrerPw] = useState('');

  // Native Android biometri state
  const [native_har_credentials, setNativeHarCredentials] = useState(false);
  const [native_biometri_tilgaengelig, setNativeBiometriTilgaengelig] = useState(false);
  const [native_email, setNativeEmail] = useState('');
  const [native_pw, setNativePw] = useState('');
  const [native_gem_loader, setNativeGemLoader] = useState(false);
  const [native_slet_loader, setNativeSletLoader] = useState(false);

  const hent_passkeys = useCallback(async () => {
    setLoading(true);
    setFejl(null);
    try {
      const res = await fetch('/api/auth/passkey/mine', { credentials: 'include' });
      if (!res.ok) throw new Error('Kunne ikke hente passkeys');
      const data = await res.json();
      setPasskeys(data.passkeys || []);
    } catch (e) {
      setFejl(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Tjek native biometri status ved mount
  useEffect(() => {
    if (er_native_android) {
      (async () => {
        try {
          const { tilgaengelig } = await tjekBiometrisk();
          setNativeBiometriTilgaengelig(tilgaengelig);
          if (tilgaengelig) {
            const har = await harGemteCredentials();
            setNativeHarCredentials(har);
          }
        } catch { /* ingen biometri */ }
        setLoading(false);
      })();
    } else if (PASSKEY_UNDERSTOETTES) {
      hent_passkeys();
    } else {
      setLoading(false);
    }
  }, [hent_passkeys]);

  const registrer_passkey = async () => {
    if (!PASSKEY_UNDERSTOETTES) return;
    if (!registrer_pw) {
      setFejl('Bekræft din nuværende adgangskode for at registrere en ny passkey');
      return;
    }
    setRegistrerer(true); setFejl(null); setBesked(null);
    try {
      // Trin 1: Hent registration options fra backend — kræver nu adgangskode-
      // bekræftelse (SIKKERHEDSFIX 2026-10-09, se kommentar ved state ovenfor).
      const start_res = await fetch('/api/auth/passkey/registrer-start', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adgangskode: registrer_pw }),
      });
      if (!start_res.ok) {
        const d = await start_res.json();
        throw new Error(d.fejl || 'Kunne ikke starte registrering');
      }
      const options = await start_res.json();

      // Trin 2: Bed browser/autentikator om at oprette credential
      const { startRegistration } = await hent_webauthn_browser();
      let attestation;
      try {
        attestation = await startRegistration({ optionsJSON: options });
      } catch (err) {
        if (err.name === 'NotAllowedError') throw new Error('Registrering annulleret');
        throw new Error('Passkey-registrering mislykkedes: ' + err.message);
      }

      // Trin 3: Send attestation til backend
      const faerdig_res = await fetch('/api/auth/passkey/registrer-faerdig', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response: attestation,
          navn: nyt_navn.trim() || undefined,
        }),
      });
      const data = await faerdig_res.json();
      if (!faerdig_res.ok) throw new Error(data.fejl || 'Registrering fejlede');

      setBesked('Passkey registreret! Du kan nu logge ind med fingeraftryk/Face ID.');
      setNytNavn('');
      setRegistrerPw('');
      await hent_passkeys();
    } catch (e) {
      setFejl(e.message);
      setRegistrerPw(''); // ryd altid adgangskodefeltet, succes eller fejl
    } finally {
      setRegistrerer(false);
    }
  };

  const slet_passkey = async (id) => {
    setSletterID(id); setFejl(null); setBesked(null);
    try {
      const res = await fetch(`/api/auth/passkey/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.fejl || 'Sletning mislykkedes');
      }
      setBesked('Passkey slettet.');
      await hent_passkeys();
    } catch (e) {
      setFejl(e.message);
    } finally {
      setSletterID(null);
    }
  };

  // ── NATIVE ANDROID: Gem / slet fingeraftryk-credentials ──
  const native_gem = async () => {
    if (!native_email || !native_pw) {
      setFejl('Udfyld email og adgangskode for at aktivere fingeraftryk-login');
      return;
    }
    setNativeGemLoader(true); setFejl(null); setBesked(null);
    try {
      await gemCredentialsMedBiometri(native_email, native_pw);
      setNativeHarCredentials(true);
      setBesked('Fingeraftryk-login aktiveret! Du kan nu logge ind med fingeraftryk.');
      setNativeEmail(''); setNativePw('');
    } catch (e) {
      setFejl('Kunne ikke gemme: ' + (e?.message || 'ukendt fejl'));
    } finally {
      setNativeGemLoader(false);
    }
  };

  const native_slet = async () => {
    setNativeSletLoader(true); setFejl(null); setBesked(null);
    try {
      await sletGemteCredentials();
      setNativeHarCredentials(false);
      setBesked('Fingeraftryk-login er deaktiveret.');
    } catch (e) {
      setFejl('Kunne ikke slette: ' + (e?.message || 'ukendt fejl'));
    } finally {
      setNativeSletLoader(false);
    }
  };

  // ── NATIVE ANDROID UI ──
  if (er_native_android) {
    return (
      <div style={styles.container}>
        <h3 style={styles.overskrift}>Fingeraftryk-login</h3>
        {loading ? (
          <p style={styles.tekst_dæmpet}>Tjekker biometri...</p>
        ) : !native_biometri_tilgaengelig ? (
          <p style={styles.tekst_dæmpet}>
            Fingeraftryk er ikke konfigureret på denne enhed. Opsæt fingeraftryk i
            Android-indstillinger for at aktivere denne funktion.
          </p>
        ) : (
          <>
            <p style={styles.tekst_dæmpet}>
              {native_har_credentials
                ? 'Fingeraftryk-login er aktivt på denne enhed. Du kan deaktivere det nedenfor.'
                : 'Aktiver fingeraftryk-login ved at indtaste dine loginoplysninger. De gemmes krypteret på din enhed.'}
            </p>
            {fejl && <div style={styles.fejl_boks}>{fejl}</div>}
            {besked && <div style={styles.ok_boks}>{besked}</div>}
            {native_har_credentials ? (
              <button
                onClick={native_slet}
                disabled={native_slet_loader}
                style={{ ...styles.slet_knap, width: '100%', padding: '12px', fontSize: 14 }}
              >
                {native_slet_loader ? 'Sletter...' : 'Deaktiver fingeraftryk-login'}
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  type="email"
                  value={native_email}
                  onChange={e => setNativeEmail(e.target.value)}
                  placeholder="Din email"
                  autoComplete="email"
                  style={styles.input}
                />
                <input
                  type="password"
                  value={native_pw}
                  onChange={e => setNativePw(e.target.value)}
                  placeholder="Din adgangskode"
                  autoComplete="current-password"
                  style={styles.input}
                />
                <button
                  onClick={native_gem}
                  disabled={native_gem_loader || !native_email || !native_pw}
                  style={native_gem_loader ? { ...styles.knap, ...styles.knap_disabled } : styles.knap}
                >
                  {native_gem_loader ? 'Aktiverer...' : 'Aktiver fingeraftryk-login'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (!PASSKEY_UNDERSTOETTES) {
    return (
      <div style={styles.container}>
        <h3 style={styles.overskrift}>Passkey / Biometrisk login</h3>
        <p style={styles.tekst_dæmpet}>
          Din browser understøtter ikke passkeys. Brug en moderne browser (Chrome, Safari,
          Edge) på en enhed med fingeraftryk eller Face ID.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.overskrift}>Passkey / Biometrisk login</h3>
      <p style={styles.tekst_dæmpet}>
        Registrer en passkey på denne enhed for at logge ind med fingeraftryk eller Face ID
        — ingen adgangskode nødvendig.
      </p>

      {fejl && <div style={styles.fejl_boks}>{fejl}</div>}
      {besked && <div style={styles.ok_boks}>{besked}</div>}

      {/* Registrer ny passkey — kræver adgangskode-bekræftelse (SIKKERHEDSFIX 2026-10-09) */}
      <div style={styles.registrer_sektion}>
        <input
          type="text"
          value={nyt_navn}
          onChange={e => setNytNavn(e.target.value)}
          placeholder={'Navn på denne enhed (fx "Min iPhone 15")'}
          maxLength={100}
          style={styles.input}
        />
        <input
          type="password"
          value={registrer_pw}
          onChange={e => setRegistrerPw(e.target.value)}
          placeholder="Bekræft din adgangskode"
          autoComplete="current-password"
          style={styles.input}
        />
        <button
          onClick={registrer_passkey}
          disabled={registrerer || !registrer_pw}
          style={(registrerer || !registrer_pw) ? { ...styles.knap, ...styles.knap_disabled } : styles.knap}
        >
          {registrerer ? 'Registrerer...' : 'Tilføj passkey på denne enhed'}
        </button>
      </div>

      {/* Liste over eksisterende passkeys */}
      {loading ? (
        <p style={styles.tekst_dæmpet}>Henter passkeys...</p>
      ) : passkeys.length === 0 ? (
        <p style={styles.tekst_dæmpet}>Du har ingen passkeys endnu.</p>
      ) : (
        <div style={styles.liste}>
          <div style={styles.liste_overskrift}>Dine passkeys</div>
          {passkeys.map(pk => (
            <div key={pk.id} style={styles.liste_item}>
              <div style={{ flex: 1 }}>
                <div style={styles.pk_navn}>{pk.navn || 'Passkey'}</div>
                <div style={styles.pk_meta}>
                  Oprettet: {new Date(pk.created_at).toLocaleDateString('da-DK')}
                  {pk.last_used_at && (
                    <> · Sidst brugt: {new Date(pk.last_used_at).toLocaleDateString('da-DK')}</>
                  )}
                </div>
              </div>
              <button
                onClick={() => slet_passkey(pk.id)}
                disabled={sletter_id === pk.id}
                style={styles.slet_knap}
              >
                {sletter_id === pk.id ? '...' : 'Slet'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline styles — mørkt tema kompatibelt med sagsportalens dark UI
const styles = {
  container: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 14,
    padding: '20px 24px',
    marginTop: 16,
  },
  overskrift: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e2e8f0',
    margin: '0 0 8px 0',
  },
  tekst_dæmpet: {
    fontSize: 13,
    color: 'rgba(148,163,184,0.8)',
    lineHeight: 1.5,
    margin: '0 0 14px 0',
  },
  fejl_boks: {
    background: 'rgba(255,107,107,0.1)',
    border: '1px solid rgba(255,107,107,0.3)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: '#f87171',
    marginBottom: 14,
  },
  ok_boks: {
    background: 'rgba(16,185,129,0.1)',
    border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: '#10b981',
    marginBottom: 14,
  },
  registrer_sektion: {
    display: 'flex',
    gap: 10,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  input: {
    flex: 1,
    minWidth: 180,
    background: 'rgba(8,12,30,0.65)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 9,
    padding: '11px 13px',
    color: '#e2e8f0',
    fontSize: 13,
    fontFamily: "'Inter',system-ui,sans-serif",
    outline: 'none',
    boxSizing: 'border-box',
  },
  knap: {
    background: 'linear-gradient(135deg,#4f46e5 0%,#3fd4c7 100%)',
    border: 'none',
    borderRadius: 9,
    padding: '11px 18px',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: "'Inter',system-ui,sans-serif",
    whiteSpace: 'nowrap',
  },
  knap_disabled: {
    background: 'rgba(79,70,229,0.4)',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  liste: {
    marginTop: 4,
  },
  liste_overskrift: {
    fontSize: 11,
    color: 'rgba(148,163,184,0.6)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  liste_item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10,
    marginBottom: 8,
  },
  pk_navn: {
    fontSize: 14,
    fontWeight: 600,
    color: '#e2e8f0',
  },
  pk_meta: {
    fontSize: 11,
    color: 'rgba(148,163,184,0.7)',
    marginTop: 2,
  },
  slet_knap: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 7,
    padding: '6px 12px',
    color: '#f87171',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: "'Inter',system-ui,sans-serif",
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
};
